import { useState } from "react";
import { Dropdown, Space } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { BiPlus, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import { DATA_BINDING_MODES } from "utils/constants";
import CanvasIcon from "./CanvasIcon";
import { ICON_PATHS } from "./step-look";

const FilterList = ({ value, onChange, labels }) => {
  const rows = value || [];
  const update = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const remove = (index) => onChange(rows.filter((_, i) => i !== index));
  const add = () => onChange([...rows, { field: "", value: "" }]);

  return (
    <Space direction="vertical" size={6} className="canvas-editor__full-width">
      {rows.map((row, index) => (
        <div key={index} className="canvas-editor__list-row">
          <Input
            placeholder={labels.FILTER_FIELD}
            value={get(row, "field", "")}
            onChange={(e) => update(index, { field: e.target.value })}
          />
          <Input
            placeholder={labels.FILTER_VALUE}
            value={get(row, "value", "")}
            onChange={(e) => update(index, { value: e.target.value })}
          />
          <div className="delete-icon" onClick={() => remove(index)}>
            <BiTrash color={COLORS.C_DANGER} size={16} />
          </div>
        </div>
      ))}
      <Button type="primary--outlined" onClick={add}>
        <BiPlus size={16} /> {labels.ADD_FILTER}
      </Button>
    </Space>
  );
};

const DataBindingListField = ({ value, onChange, labels, listLabels }) => {
  const [editing, setEditing] = useState({});
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const DATA = t("FORM.STATUSES.DATA", { returnObjects: true });
  const modeOptions = [
    { value: DATA_BINDING_MODES.ONE, label: DATA.MODE_ONE },
    { value: DATA_BINDING_MODES.MANY, label: DATA.MODE_MANY },
  ];

  const { data } = useQuery(graphql.queries.DATA_ENTITIES);
  const entities = get(data, "dataEntities") || [];
  const tokens = get(data, "dataBindingTokens") || [];
  const entityOptions = entities.map((entity) => ({
    value: entity.key,
    label: `${entity.label} (${entity.key})`,
  }));

  const items = value || [];
  const update = (index, patch) =>
    onChange(
      items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const add = () =>
    onChange([
      ...items,
      {
        key: "",
        entity: null,
        mode: DATA_BINDING_MODES.ONE,
        filters: [],
      },
    ]);

  const pickEntity = (index, entityKey) => {
    const entity = entities.find((candidate) => candidate.key === entityKey);
    update(index, {
      entity: entityKey,
      mode: get(entity, "defaultMode") || DATA_BINDING_MODES.ONE,
      filters: get(entity, "defaultFilters") || [],
    });
  };

  const summaryOf = (item) => {
    const entity = entities.find((candidate) => candidate.key === item.entity);
    const mode =
      item.mode === DATA_BINDING_MODES.MANY ? DATA.MODE_MANY : DATA.MODE_ONE;
    return {
      entity: get(entity, "label") || item.entity || listLabels.UNSET,
      mode,
      filters: (item.filters || [])
        .map((filter) => `${filter.field} = ${filter.value}`)
        .join(", "),
    };
  };

  const toggle = (index) =>
    setEditing((current) => ({ ...current, [index]: !current[index] }));

  const addAndEdit = () => {
    setEditing((current) => ({ ...current, [items.length]: true }));
    add();
  };

  return (
    <div className="canvas-rows">
      {items.length === 0 ? (
        <p className="canvas-tab__hint">{listLabels.EMPTY}</p>
      ) : null}
      {items.map((item, index) => {
        const summary = summaryOf(item);
        return (
          <div key={index} className="canvas-row-card">
            <div className="canvas-row-card__head canvas-row-card__head--top">
              <span className="canvas-row-card__icon">
                <CanvasIcon
                  path={ICON_PATHS.database}
                  size={16}
                  strokeWidth={2}
                />
              </span>
              <span className="canvas-row-card__summary">
                <span>
                  <strong>{summary.entity}</strong>{" "}
                  <span className="canvas-next__muted">
                    · {summary.mode} · {listLabels.NAMED}
                  </span>{" "}
                  <code>{item.key}</code>
                </span>
                {summary.filters ? (
                  <span className="canvas-row-card__muted">
                    {`${listLabels.FILTER} ${summary.filters}`}
                  </span>
                ) : null}
              </span>
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "edit",
                      label: editing[index]
                        ? listLabels.CLOSE
                        : listLabels.EDIT,
                    },
                    { key: "delete", label: listLabels.DELETE, danger: true },
                  ],
                  onClick: ({ key }) =>
                    key === "edit" ? toggle(index) : remove(index),
                }}
              >
                <button
                  type="button"
                  className="canvas-next__more"
                  aria-label={listLabels.ACTIONS}
                >
                  <CanvasIcon
                    path={ICON_PATHS.dots}
                    size={18}
                    strokeWidth={3}
                  />
                </button>
              </Dropdown>
            </div>
            {editing[index] ? (
              <div className="canvas-row-card__editor">
                <Input
                  label={labels.KEY}
                  placeholder={labels.KEY}
                  value={item.key || ""}
                  onChange={(e) => update(index, { key: e.target.value })}
                />
                <div className="canvas-editor__field-block">
                  <Select
                    showSearch
                    label={labels.ENTITY}
                    placeholder={labels.ENTITY}
                    options={entityOptions}
                    value={item.entity || undefined}
                    onChange={(entityKey) => pickEntity(index, entityKey)}
                  />
                </div>
                <div className="canvas-editor__field-block">
                  <Select
                    label={labels.MODE}
                    placeholder={labels.MODE}
                    options={modeOptions}
                    value={item.mode || DATA_BINDING_MODES.ONE}
                    onChange={(mode) => update(index, { mode })}
                  />
                </div>
                <div className="canvas-editor__card-title">
                  {labels.FILTERS}
                </div>
                <FilterList
                  value={item.filters}
                  onChange={(filters) => update(index, { filters })}
                  labels={labels}
                />
                {tokens.length ? (
                  <div className="canvas-editor__meta-xs">
                    {labels.TOKENS}:{" "}
                    {tokens.map((token) => `{{${token}}}`).join(" · ")}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      <button
        type="button"
        className="canvas-dashed-button canvas-dashed-button--small"
        onClick={addAndEdit}
      >
        {listLabels.ADD || labels.ADD}
      </button>
    </div>
  );
};

DataBindingListField.defaultProps = {
  listLabels: {},
};

export default DataBindingListField;
